/**
 * LocationsLeaderboard — QSR multi-store league table (light theme).
 * Ranks stores by SPLH with network benchmarks, animated rank bars, a per-row
 * trend line and a Corvana AI insight. Rows are clickable → open LocationDrawer.
 */
import React, { useMemo } from 'react';
import { Trophy, TrendingUp, TrendingDown, Timer, DollarSign, Target, Sparkles, Gauge, ChevronRight } from 'lucide-react';
import Sparkline from './qsr/Sparkline';
import AskCorvana from './qsr/AskCorvana';
import { QSR_STORES, TARGETS, avgBy, money } from './qsr/qsrDemoData';

const RANK = [
  { ring: 'ring-amber-400', bg: 'bg-amber-50', text: 'text-amber-700', label: '1st' },
  { ring: 'ring-slate-400', bg: 'bg-slate-100', text: 'text-slate-700', label: '2nd' },
  { ring: 'ring-orange-400', bg: 'bg-orange-50', text: 'text-orange-700', label: '3rd' },
];

export default function LocationsLeaderboard({ stores, onSelectStore }) {
  const data = (stores && stores.length ? stores : QSR_STORES);
  const ranked = useMemo(() => [...data].sort((a, b) => b.splh - a.splh), [data]);
  const netSplh = avgBy('splh', data);
  const netSos = avgBy('sos', data);
  const netAcc = avgBy('accuracy', data);
  const best = ranked[0];
  const worst = ranked[ranked.length - 1];
  const maxSplh = Math.max(...data.map((s) => s.splh));

  return (
    <div className="rounded-3xl qsr-card p-5 sm:p-6 shadow-sm" data-testid="locations-leaderboard">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-amber-50 border border-amber-200">
            <Trophy className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-base tracking-tight text-slate-900">Store league table</h3>
            <p className="text-xs text-slate-500">Ranked by Sales per Labour Hour · {data.length} locations</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
          <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
          Live network
        </span>
        <AskCorvana insight={`${best.name} is your benchmark store. The fastest network win is closing ${worst.name}'s gap — speed of service and labour shape are the two levers.`} />
      </div>

      {/* Network benchmarks */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { icon: Gauge, label: 'Network SPLH', val: `$${netSplh.toFixed(1)}`, sub: `target $${TARGETS.splh}.0` },
          { icon: Timer, label: 'Avg speed of service', val: `${netSos.toFixed(1)}m`, sub: `target ${TARGETS.sos}m` },
          { icon: Target, label: 'Order accuracy', val: `${netAcc.toFixed(1)}%`, sub: `target ${TARGETS.accuracy}%` },
        ].map((b) => (
          <div key={b.label} className="rounded-2xl bg-slate-50 border border-slate-200 p-3">
            <b.icon className="w-4 h-4 text-emerald-600 mb-1.5" />
            <div className="text-lg font-semibold tracking-tight text-slate-900">{b.val}</div>
            <div className="text-[11px] text-slate-600 leading-tight">{b.label}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{b.sub}</div>
          </div>
        ))}
      </div>

      {/* Ranked stores (clickable) */}
      <div className="space-y-2.5">
        {ranked.map((s, i) => {
          const r = RANK[i] || RANK[2];
          const pct = Math.round((s.splh / maxSplh) * 100);
          const vsNet = s.splh - netSplh;
          return (
            <button
              key={s.id || s.name}
              onClick={() => onSelectStore?.(s)}
              className="w-full text-left rounded-2xl bg-slate-50 border border-slate-200 p-3.5 hover:border-slate-300 hover:bg-white transition-colors group"
              data-testid={`leaderboard-row-${(s.id || s.name).toLowerCase()}`}
            >
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className={`shrink-0 h-8 w-8 grid place-items-center rounded-full ring-2 ${r.ring} ${r.bg} ${r.text} text-xs font-bold`}>{r.label}</span>
                <span className="font-semibold text-sm text-slate-900">{s.name}</span>
                <span className={`ml-auto inline-flex items-center gap-1 text-xs font-semibold ${vsNet >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {vsNet >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  {vsNet >= 0 ? '+' : ''}{vsNet.toFixed(1)} vs network
                </span>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
              </div>
              <div className="h-2 rounded-full bg-slate-200 overflow-hidden mb-2.5">
                <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-[width] duration-1000 ease-out" style={{ width: `${pct}%` }} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <Metric icon={DollarSign} label="SPLH" value={`$${s.splh.toFixed(1)}`} />
                <Metric icon={Timer} label="Speed" value={`${s.sos.toFixed(1)}m`} />
                <Metric icon={Target} label="Accuracy" value={`${s.accuracy.toFixed(1)}%`} />
                <Metric icon={DollarSign} label="Wk sales" value={money(s.weeklySales)} />
              </div>
            </button>
          );
        })}
      </div>

      {/* AI analysis */}
      <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-3.5 flex gap-3">
        <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-700 leading-relaxed">
          <span className="font-semibold text-slate-900">Corvana AI:</span> {best.name} leads on SPLH (${best.splh.toFixed(1)}) —
          {' '}~{(((best.splh - worst.splh) / worst.splh) * 100).toFixed(0)}% ahead of {worst.name}. Closing {worst.name}'s
          speed of service from {worst.sos.toFixed(1)}m toward the {netSos.toFixed(1)}m network average could recover an
          estimated <span className="font-semibold text-emerald-700">{money((worst.weeklySales) * 0.06)}/wk</span> in
          drive‑through throughput. Labour % at {worst.name} ({worst.labourPct.toFixed(1)}%) is the biggest lever.
        </p>
      </div>

      {/* Benchmarks + data sources */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500 border-t border-slate-100 pt-2.5" data-testid="leaderboard-benchmarks">
        <span className="font-semibold text-slate-600 uppercase tracking-wide">Benchmarks</span>
        <span>SPLH target ${TARGETS.splh}</span>
        <span>Speed of service {TARGETS.sos}m</span>
        <span>Order accuracy {TARGETS.accuracy}%</span>
        <span className="text-slate-400">· Sources: Lightspeed POS/KDS, Tanda, Doshii, ATO retail-food</span>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 px-2.5 py-1.5">
      <div className="flex items-center gap-1 text-[10px] text-slate-500"><Icon className="w-3 h-3" />{label}</div>
      <div className="font-semibold text-sm text-slate-900">{value}</div>
    </div>
  );
}
